import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Pagination,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  ViewModule,
  ViewList,
  FilterList,
} from '@mui/icons-material';
import PropertyCard from './PropertyCard';
import PropertyFilters from './PropertyFilters';
import { Property, PropertySearchFilters } from '@/types';
import { PropertyService } from '@/services/propertyService';
import { QueryDocumentSnapshot } from 'firebase/firestore';

interface PropertyListProps {
  properties?: Property[];
  loading?: boolean;
  error?: string | null;
  onPropertyClick?: (property: Property) => void;
  onPropertyEdit?: (property: Property) => void;
  onPropertyDelete?: (property: Property) => void;
  showFilters?: boolean;
  filters?: PropertySearchFilters;
  onFilterChange?: (filters: PropertySearchFilters) => void;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  compact?: boolean;
  enableInfiniteScroll?: boolean;
  pagination?: {
    page: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    onPageChange: (page: number) => void;
  };
}

const PropertyList: React.FC<PropertyListProps> = ({
  properties: externalProperties,
  loading: externalLoading,
  error: externalError,
  onPropertyClick,
  onPropertyEdit,
  onPropertyDelete,
  showFilters = true,
  filters: externalFilters,
  onFilterChange: externalOnFilterChange,
  viewMode: externalViewMode,
  onViewModeChange,
  compact = false,
  enableInfiniteScroll = false,
  pagination,
}) => {
  const [properties, setProperties] = useState<Property[]>(externalProperties || []);
  const [loading, setLoading] = useState(externalLoading || false);
  const [error, setError] = useState<string | null>(externalError || null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(externalViewMode || 'grid');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters] = useState<PropertySearchFilters>(externalFilters || {});
  const [currentPage, setCurrentPage] = useState(pagination?.page || 1);
  const [totalPages, setTotalPages] = useState(pagination?.totalPages || 1);
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'createdAt',
    direction: 'desc',
  });
  
  // Infinite scroll state
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Use external properties if provided, otherwise fetch
  const isControlled = externalProperties !== undefined;

  useEffect(() => {
    if (externalProperties) {
      setProperties(externalProperties);
    }
  }, [externalProperties]);

  useEffect(() => {
    if (externalLoading !== undefined) {
      setLoading(externalLoading);
    }
  }, [externalLoading]);

  useEffect(() => {
    if (externalError !== undefined) {
      setError(externalError);
    }
  }, [externalError]);

  useEffect(() => {
    if (externalFilters) {
      setFilters(externalFilters);
    }
  }, [externalFilters]);

  useEffect(() => {
    if (externalViewMode) {
      setViewMode(externalViewMode);
    }
  }, [externalViewMode]);

  const fetchProperties = useCallback(async (page: number = 1, append: boolean = false) => {
    if (isControlled) return;

    setLoading(true);
    setError(null);

    try {
      if (enableInfiniteScroll) {
        // Cursor-based pagination for infinite scroll
        const options = {
          filters,
          sort,
          pagination: {
            limit: 12,
            cursor: append ? lastDoc || undefined : undefined,
          }
        };
        
        const response = await PropertyService.listProperties(options);
        
        if (response.success && response.data) {
          if (append) {
            setProperties((prev) => [...prev, ...response.data!]);
          } else {
            setProperties(response.data);
          }
          
          if (response.pagination) {
            setLastDoc(response.pagination.nextCursor || null);
            setHasMore(response.pagination.hasNext);
          }
        } else {
          setError(response.error || 'Failed to load properties');
        }
      } else {
        // Page-based pagination
        const response = await PropertyService.getProperties(filters, page, 12, sort);
        
        if (response.success && response.data) {
          if (append) {
            setProperties((prev) => [...prev, ...response.data!]);
          } else {
            setProperties(response.data);
          }
          
          if (response.pagination) {
            setCurrentPage(response.pagination.page);
            setTotalPages(response.pagination.totalPages);
          }
        } else {
          setError(response.error || 'Failed to load properties');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [isControlled, enableInfiniteScroll, filters, sort, lastDoc]);

  useEffect(() => {
    if (!isControlled) {
      // Reset and fetch when filters/sort change or initial load
      // For infinite scroll, we pass page 1 (ignored) and append=false
      setLastDoc(null);
      fetchProperties(1, false);
    }
  }, [filters, sort, isControlled]); 

  const handleFilterChange = (newFilters: PropertySearchFilters) => {
    if (externalOnFilterChange) {
      externalOnFilterChange(newFilters);
    } else {
      setFilters(newFilters);
      setCurrentPage(1); // Reset to first page when filters change
      setLastDoc(null);
    }
  };

  const handleSortChange = (event: any) => {
    const value = event.target.value;
    const [field, direction] = value.split(':');
    setSort({ field, direction: direction as 'asc' | 'desc' });
    setCurrentPage(1);
    setLastDoc(null);
  };

  const handleViewModeChange = (newMode: 'grid' | 'list') => {
    setViewMode(newMode);
    if (onViewModeChange) {
      onViewModeChange(newMode);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    if (pagination?.onPageChange) {
      pagination.onPageChange(page);
    } else if (!isControlled) {
      fetchProperties(page, false);
    }
  };

  const handleLoadMore = () => {
    if (enableInfiniteScroll) {
      if (!loading && hasMore) {
        fetchProperties(undefined, true);
      }
    } else {
      const nextPage = currentPage + 1;
      if (nextPage <= totalPages) {
        setCurrentPage(nextPage);
        fetchProperties(nextPage, true);
      }
    }
  };

  // Intersection Observer for Infinite Scroll
  useEffect(() => {
    if (!enableInfiniteScroll || !hasMore || loading || isControlled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [enableInfiniteScroll, hasMore, loading, isControlled]); 

  if (loading && properties.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && properties.length === 0) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (properties.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No properties found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {Object.keys(filters).length > 0
            ? 'Try adjusting your filters to see more results.'
            : 'Start by adding your first property.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h6">
          {properties.length} {properties.length === 1 ? 'Property' : 'Properties'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={`${sort.field}:${sort.direction}`}
              onChange={handleSortChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Sort by' }}
            >
              <MenuItem value="createdAt:desc">Newest First</MenuItem>
              <MenuItem value="createdAt:asc">Oldest First</MenuItem>
              <MenuItem value="metadata.price:desc">Price: High to Low</MenuItem>
              <MenuItem value="metadata.price:asc">Price: Low to High</MenuItem>
              <MenuItem value="metadata.squareFootage:desc">Size: Largest First</MenuItem>
              <MenuItem value="metadata.bedrooms:desc">Bedrooms: Most First</MenuItem>
            </Select>
          </FormControl>

          {showFilters && (
            <Tooltip title="Filters">
              <IconButton
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                color={showFilterPanel ? 'primary' : 'default'}
              >
                <FilterList />
              </IconButton>
            </Tooltip>
          )}

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && handleViewModeChange(newMode)}
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModule />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewList />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Filters Panel */}
      {showFilters && showFilterPanel && (
        <Box sx={{ mb: 3 }}>
          <PropertyFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClose={() => setShowFilterPanel(false)}
          />
        </Box>
      )}

      {/* Properties Grid/List */}
      {/* Only show loading spinner here if initial load, otherwise show below list */}
      {loading && properties.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && properties.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {properties.map((property) => (
          <Grid
            item
            xs={12}
            sm={viewMode === 'grid' ? 6 : 12}
            md={viewMode === 'grid' ? 4 : 12}
            lg={viewMode === 'grid' ? 3 : 12}
            key={property.id}
          >
            <PropertyCard
              property={property}
              onView={onPropertyClick}
              onEdit={onPropertyEdit}
              onDelete={onPropertyDelete}
              compact={compact || viewMode === 'list'}
            />
          </Grid>
        ))}
      </Grid>

      {/* Pagination or Load More / Infinite Scroll Sentinel */}
      {enableInfiniteScroll ? (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
           {hasMore && (
             <div ref={observerTarget} style={{ height: '20px', margin: '10px 0', width: '100%' }}>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
             </div>
           )}
           {!hasMore && properties.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No more properties to load.
              </Typography>
           )}
        </Box>
      ) : (
        totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={pagination?.page || currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )
      )}
    </Box>
  );
};

export default PropertyList;